import { queryOptions, useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query'

import { ApiCaller } from '@graphit/callers'
import type { Cartography } from '@graphit/types'

export const cartographiesQueryOptions = queryOptions({
	queryKey: ['cartographies'],
	queryFn: ApiCaller.getCartographies
})

export const useCartographiesQuery = () => useSuspenseQuery(cartographiesQueryOptions)

export const useCreateCartographyMutation = () => {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: ApiCaller.createCartography,
		onSuccess: () => queryClient.invalidateQueries({ queryKey: ['cartographies'] })
	})
}

export const useUpdateCartographyMutation = () => {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: ({ id, cartography }: { id: string; cartography: Partial<Cartography> }) =>
			ApiCaller.updateCartography(id, cartography),
		onSuccess: (_, { id }) => {
			queryClient.invalidateQueries({ queryKey: ['cartographies'] })
			queryClient.invalidateQueries({ queryKey: ['cartography', id] })
		}
	})
}

export const useDeleteCartographyMutation = () => {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: ApiCaller.deleteCartography,
		onSuccess: (_, id) => {
			queryClient.setQueryData(cartographiesQueryOptions.queryKey, old => old?.filter(c => c.id !== id))
			queryClient.removeQueries({ queryKey: ['cartography', id] })
		}
	})
}

export const useDuplicateCartographyMutation = () => {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: ApiCaller.duplicateCartography,
		onSuccess: () => queryClient.invalidateQueries({ queryKey: ['cartographies'] })
	})
}
